import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  Sparkles,
  Building2,
  Target,
  Globe,
  ExternalLink,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  Zap,
  CheckCircle2,
  Users,
  MapPin,
  Briefcase,
  ArrowLeft,
} from 'lucide-react'
import { publicAnalysisApi, PublicAnalysisResponse } from '@/services/public-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

// Progress phases for different statuses
const STATUS_PHASES: Record<string, { progress: number; label: string }> = {
  pending: { progress: 5, label: 'Initializing analysis...' },
  crawling: { progress: 30, label: 'Crawling your website...' },
  analyzing: { progress: 65, label: 'Analyzing content with AI...' },
  completed: { progress: 100, label: 'Analysis complete!' },
  failed: { progress: 100, label: 'Analysis failed' },
}

// Polling configuration
const POLL_INTERVAL_MS = 3000
const MAX_POLL_DURATION_MS = 300000 // 5 minutes

export default function AnalysisResultsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [analysis, setAnalysis] = useState<PublicAnalysisResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pollStartTime] = useState(Date.now())

  const fetchAnalysis = useCallback(async () => {
    if (!id) return

    try {
      const result = await publicAnalysisApi.getAnalysis(id)
      setAnalysis(result)
      setError(null)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number; data?: { detail?: string } } }
        if (axiosError.response?.status === 404) {
          setError('Analysis not found. It may have expired.')
        } else {
          setError(axiosError.response?.data?.detail || 'Failed to load analysis.')
        }
      } else {
        setError('Failed to load analysis.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [id])

  // Initial fetch
  useEffect(() => {
    fetchAnalysis()
  }, [fetchAnalysis])

  // Polling for in-progress analyses
  useEffect(() => {
    if (!analysis || analysis.status === 'completed' || analysis.status === 'failed') {
      return
    }

    // Check if we've exceeded max poll duration
    if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
      setError('Analysis is taking longer than expected. Please try again later.')
      return
    }

    const interval = setInterval(fetchAnalysis, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [analysis, fetchAnalysis, pollStartTime])

  const handleRetry = () => {
    navigate('/demo')
  }

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper analysisId={id}>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <Sparkles className="h-12 w-12 text-primary animate-pulse mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground">Loading analysis...</h2>
        </div>
      </PageWrapper>
    )
  }

  // Error state
  if (error) {
    return (
      <PageWrapper analysisId={id}>
        <div className="max-w-2xl mx-auto py-16">
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
              <p className="text-muted-foreground">{error}</p>
              <Button onClick={handleRetry} className="mt-4">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    )
  }

  // In-progress state
  if (analysis && analysis.status !== 'completed' && analysis.status !== 'failed') {
    const phase = STATUS_PHASES[analysis.status] || STATUS_PHASES.pending
    return (
      <PageWrapper analysisId={id}>
        <div className="max-w-2xl mx-auto py-16">
          <Card>
            <CardContent className="p-8 space-y-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Sparkles className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  Analyzing {analysis.domain}
                </h2>
                <p className="text-muted-foreground mt-2">
                  This typically takes about 60 seconds
                </p>
              </div>

              <div className="space-y-3">
                <Progress value={phase.progress} className="h-3" />
                <p className="text-center text-sm text-muted-foreground">{phase.label}</p>
              </div>

              <div className="flex justify-center gap-2 text-sm text-muted-foreground">
                <span className={analysis.status === 'crawling' || analysis.status === 'analyzing' ? 'text-primary' : ''}>
                  {analysis.status === 'crawling' || analysis.status === 'analyzing' ? (
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                  ) : null}
                  Crawling
                </span>
                <span>&rarr;</span>
                <span className={analysis.status === 'analyzing' ? 'text-primary' : ''}>
                  {analysis.status === 'analyzing' ? (
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                  ) : null}
                  Analyzing
                </span>
                <span>&rarr;</span>
                <span>Complete</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    )
  }

  // Failed state
  if (analysis?.status === 'failed') {
    return (
      <PageWrapper analysisId={id}>
        <div className="max-w-2xl mx-auto py-16">
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="text-xl font-semibold text-foreground">Analysis Failed</h2>
              <p className="text-muted-foreground">
                {analysis.error || 'We couldn\'t analyze this website. Please check the URL and try again.'}
              </p>
              <Button onClick={handleRetry} className="mt-4">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    )
  }

  // Completed state - show results
  return (
    <PageWrapper analysisId={id}>
      <div className="max-w-4xl mx-auto py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <Badge variant="secondary" className="text-sm">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Analysis Complete
          </Badge>
        </div>

        {/* Domain Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">{analysis?.domain}</h1>
          <p className="text-muted-foreground">
            <Globe className="h-4 w-4 inline mr-1" />
            {analysis?.website_url}
          </p>
          {analysis && analysis.confidence > 0 && (
            <p className="text-sm text-muted-foreground">
              Confidence: {Math.round(analysis.confidence * 100)}%
            </p>
          )}
        </div>

        {/* Company Profile Card */}
        {analysis?.company_profile && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Company Profile
              </CardTitle>
              <CardDescription>
                What we learned about your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysis.company_profile.suggested_name && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Company Name</h4>
                  <p className="text-lg font-semibold">{analysis.company_profile.suggested_name}</p>
                </div>
              )}

              {analysis.company_profile.business_category && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Business Category</h4>
                  <Badge variant="outline" className="text-sm capitalize">
                    {analysis.company_profile.business_category.replace(/_/g, ' ')}
                  </Badge>
                </div>
              )}

              {analysis.company_profile.industry_tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Industry & Activities</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.company_profile.industry_tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {analysis.company_profile.target_markets.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Target Markets</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.company_profile.target_markets.map((market, i) => (
                      <Badge key={i} variant="outline">{market}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {analysis.company_profile.prospecting_goals.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    <Target className="h-4 w-4 inline mr-1" />
                    Prospecting Goals
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.company_profile.prospecting_goals.map((goal, i) => (
                      <Badge key={i} className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        {goal}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ICP Card */}
        {analysis?.ideal_customer_profile && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-green-600" />
                Ideal Customer Profile
              </CardTitle>
              <CardDescription>
                Who your best customers are
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysis.ideal_customer_profile.value_proposition && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Value Proposition</h4>
                  <p className="text-foreground">{analysis.ideal_customer_profile.value_proposition}</p>
                </div>
              )}

              <Separator />

              <div className="grid md:grid-cols-2 gap-6">
                {analysis.ideal_customer_profile.services_offered.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Briefcase className="h-4 w-4" />
                      Services Offered
                    </h4>
                    <ul className="space-y-1">
                      {analysis.ideal_customer_profile.services_offered.map((service, i) => (
                        <li key={i} className="text-sm text-foreground">&bull; {service}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.ideal_customer_profile.target_customer_industries.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      Target Industries
                    </h4>
                    <ul className="space-y-1">
                      {analysis.ideal_customer_profile.target_customer_industries.map((ind, i) => (
                        <li key={i} className="text-sm text-foreground">&bull; {ind}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.ideal_customer_profile.target_company_types.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      Company Types
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {analysis.ideal_customer_profile.target_company_types.map((type, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{type}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.ideal_customer_profile.target_company_size.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Company Size
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {analysis.ideal_customer_profile.target_company_size.map((size, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{size}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.ideal_customer_profile.buyer_persona.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Buyer Personas
                    </h4>
                    <ul className="space-y-1">
                      {analysis.ideal_customer_profile.buyer_persona.map((persona, i) => (
                        <li key={i} className="text-sm text-foreground">&bull; {persona}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.ideal_customer_profile.geographic_focus.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      Geographic Focus
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {analysis.ideal_customer_profile.geographic_focus.map((geo, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{geo}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Suggested Sources Card */}
        {analysis?.suggested_sources && analysis.suggested_sources.sources.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Recommended Data Sources
              </CardTitle>
              <CardDescription>
                B2B platforms and directories where you can find prospects
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysis.suggested_sources.reasoning && (
                <Alert className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <AlertDescription className="text-purple-800 dark:text-purple-200">
                    {analysis.suggested_sources.reasoning}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                {analysis.suggested_sources.sources.slice(0, 10).map((source, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-foreground truncate">{source.name}</h4>
                        <Badge variant="outline" className="text-xs capitalize">
                          {source.source_type.replace(/_/g, ' ')}
                        </Badge>
                        {source.region !== 'global' && (
                          <Badge variant="secondary" className="text-xs">{source.region}</Badge>
                        )}
                      </div>
                      {source.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {source.description}
                        </p>
                      )}
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-2"
                      >
                        Visit Source
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="text-sm font-medium text-muted-foreground">
                        {source.relevance_score}/10
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Card */}
        <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-2">
              <Zap className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">
              Ready to Start Prospecting?
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Sign up for free to automatically scrape these data sources, qualify leads,
              and send personalized outreach at scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Link to={`/login?analysis=${analysis?.id}`}>
                <Button size="lg" className="min-w-[200px]">
                  Get Started Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  )
}

// Page wrapper component for consistent layout
function PageWrapper({ children, analysisId }: { children: React.ReactNode; analysisId?: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/demo" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg">Prospect Hunter</span>
          </Link>
          <Link to={analysisId ? `/login?analysis=${analysisId}` : '/login'}>
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4">{children}</main>

      {/* Footer */}
      <footer className="border-t py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Prospect Hunter. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
